//
// Copyright (c) 2011-2021 by Curalate, Inc.
//

import React, { useEffect, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { useSmartInputContext } from '@components/molecules/smart-input/SmartInputContext';
import { FacebookMentionWrapper } from '@components/molecules/smart-input/mentions/FacebookMentionWrapper';
import {
  IDraftDecoratorComponentProps,
  IFacebookPageSuggestion,
} from '@components/molecules/smart-input/smartInputModel';
import { SmallTooltip } from '@components/molecules/tooltip/Tooltip';
import { extractFacebookPageIdFromTag } from '@utils/social-text-utils/facebookTextUtils';

interface Props extends IDraftDecoratorComponentProps {
  children: React.ReactNode;
}

export const FacebookPageMention = ({ children, decoratedText }: Props) => {
  const smartInputContext = useSmartInputContext();

  const [facebookPage, setFacebookPage] = useState<IFacebookPageSuggestion>();
  const [hasErrorInGettingPageMention, setHasErrorInGettingPageMention] =
    useState(false);
  const textRef = useRef<HTMLSpanElement>(null);

  const debouncedGetPageSuggestion = useDebouncedCallback(
    async (facebookPageMention: string) => {
      // The decorated text will come in with the @[page_id] syntax. This line
      // will grab only the page_id.
      const pageId = extractFacebookPageIdFromTag(facebookPageMention);
      const page =
        await smartInputContext.mentions?.getFacebookPageById?.(pageId);

      setFacebookPage(page);
      setHasErrorInGettingPageMention(!page);
    },
    500,
  );

  // Handle getting the page_id from the page mention syntax when it mounts/changes
  useEffect(() => {
    void debouncedGetPageSuggestion(decoratedText);
  }, [debouncedGetPageSuggestion, decoratedText]);

  return (
    <SmallTooltip
      appendTarget={'body'}
      content={
        hasErrorInGettingPageMention || !facebookPage ? (
          'This is an invalid page id and will not successfully tag a page.'
        ) : (
          <>
            {`This will be converted to a mention for `}
            <PrimaryAnchor href={facebookPage.link} target='_blank'>
              {facebookPage.name}
            </PrimaryAnchor>
            {` when posted.`}
          </>
        )
      }
      isDisabled={false}
      target={
        <FacebookMentionWrapper
          ref={textRef}
          hasError={hasErrorInGettingPageMention}
        >
          {children}
        </FacebookMentionWrapper>
      }
    />
  );
};
